import { Box, InputAdornment, TextField } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"
import { SearchOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import React from "react"

import { Button } from "@/components/common/Button"
import TypeaheadInput from "@/components/common/TypeaheadInput"
import type { AutocompleteOption } from "@/types/form-fields"

const SearchSuggestion = ({
  onSearch,
  placeholder,
  setInputValue,
  options,
  inputValue,
  fetching,
  sx,
}: {
  onSearch: (searchedItem: string | AutocompleteOption) => void
  placeholder: string
  inputValue: string
  setInputValue: (value: string) => void
  options: AutocompleteOption[]
  fetching: boolean
  sx?: SxProps<Theme>
}): ReactElement => {
  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value)
  }

  const handleOptionSelect = (
    _event: React.SyntheticEvent,
    value: AutocompleteOption[] | AutocompleteOption | null
  ) => {
    if (value && !Array.isArray(value)) {
      setInputValue(value.label)
      onSearch(value)
    }
  }

  const searchButtonEnabled = inputValue?.length > 1

  return (
    <Box className='flex w-full items-center gap-4' data-test='search-suggestion'>
      <TypeaheadInput<AutocompleteOption>
        options={options}
        getOptionLabel={(option) => option?.label ?? option}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        loading={fetching}
        inputValue={inputValue || ""}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchOutlined className='text-gray-600' />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        sx={{
          width: 524,
          ...sx,
        }}
      />
      <Button
        startIcon={<SearchOutlined className='text-white' />}
        onClick={() => onSearch(inputValue)}
        disabled={!searchButtonEnabled}
      >
        Search
      </Button>
    </Box>
  )
}

export default SearchSuggestion
